// src/components/CottageList.jsx
import React, { useEffect, useState } from "react";
import { translateApiMessage } from "../utils/formErrors";
import { Link, useNavigate } from "react-router-dom";
import { formatNumber } from "../utils/numberFormat";
import PaginationControls from "./PaginationControls";
import HotToastMessage from "./ui/HotToastMessage";
import { Search } from "lucide-react";
import axiosInstance from "../utils/axiosInstance";

const QuotaPermitedList = () => {
  const navigate = useNavigate();

  // ---- Auth guard
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) navigate("/");
  }, [navigate]);

  // ---- Local UI state
  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [count, setCount] = useState(0);

  const [toast, setToast] = useState({ message: "", type: "info" });

  const totalPages = count ? Math.ceil(count / pageSize) : 1;

  // ---- Debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      setQuery(searchText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchText]);

  // ---- Fetch data directly with axios from /active-quota-permits/
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.append("page", currentPage);
        params.append("page_size", pageSize);
        if (query) params.append("search", query);

        const response = await axiosInstance.get(
          `/active-quota-permits/?${params.toString()}`
        );

        setItems(response.data.results || []);
        setNext(response.data.next);
        setPrevious(response.data.previous);
        setCount(response.data.count || 0);
      } catch (err) {
        console.error("خطا در بارگذاری داده‌ها:", err);
        setError(
          translateApiMessage(err.response?.data?.detail) ||
            "خطا در ارتباط با سرور. لطفاً دوباره تلاش کنید."
        );
        setToast({
          message: "خطا در بارگذاری اطلاعات.",
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, pageSize, query]);

  // ---- Pagination handlers
  const hasNext = !!next;
  const hasPrevious = !!previous;
  const handlePageChange = (e) => setCurrentPage(Number(e.target.value));
  const handleNextPage = () => hasNext && setCurrentPage((p) => p + 1);
  const handlePreviousPage = () => hasPrevious && setCurrentPage((p) => p - 1);
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="mt-24 pb-20 bg-gray-50 min-h-screen font-vazir" dir="rtl">
      {/* Toast */}
      <HotToastMessage
        message={toast.message}
        type={toast.type}
        duration={3000}
        onClose={() => setToast({ message: "", type: "info" })}
        position="top"
      />

      {/* Container card */}
      <div className="mx-auto p-4 sm:p-6 bg-white/70 backdrop-blur-md rounded-2xl shadow-sm border border-white/30">
        {/* Top bar */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              مدیریت سهمیه
            </h2>
            <p className="text-gray-500 text-sm">
              لیست سهمیه‌های فعال و مصرف‌شده
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 text-right mb-2">
            جستجو (شماره سفارش/ارز):
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="جستجوی خود را وارد کنید…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full pl-3 pr-10 py-3 rounded-xl border bg-white/90 focus:outline-none focus:ring-2 focus:ring-blue-500 transition text-right"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
        </div>

        {/* Loading / Error */}
        {loading && (
          <div className="mt-8 grid gap-3">
            <div className="h-10 rounded-xl bg-gray-100 animate-pulse" />
            <div className="h-10 rounded-xl bg-gray-100 animate-pulse" />
            <div className="h-10 rounded-xl bg-gray-100 animate-pulse" />
          </div>
        )}
        {error && !loading && (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        {/* Table & Pagination */}
        {!loading && !error && (
          <>
            {items.length === 0 ? (
              <div className="mt-10 text-center text-gray-600">
                موردی یافت نشد.
              </div>
            ) : (
              <>
                <div className="mt-4">
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    pageSizeOptions={[10, 20, 50, 100, 200]}
                    hasNext={hasNext}
                    hasPrevious={hasPrevious}
                    onPageChange={handlePageChange}
                    onNextPage={handleNextPage}
                    onPreviousPage={handlePreviousPage}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </div>

                <div className="mt-3 overflow-x-auto rounded-2xl border bg-white shadow-sm">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-right text-gray-700">
                        <th className="p-3 font-semibold">ردیف</th>
                        <th className="p-3 font-semibold">شماره سفارش</th>
                        <th className="p-3 font-semibold">مبلغ (ارز اصلی)</th>
                        <th className="p-3 font-semibold">ارز</th>
                        <th className="p-3 font-semibold">معادل دلار</th>
                        <th className="p-3 font-semibold">نرخ تبدیل</th>
                        <th className="p-3 font-semibold">تاریخ تبدیل</th>
                        <th className="p-3 font-semibold">مصرف‌شده</th>
                        <th className="p-3 font-semibold">
                          ارز کالا های تریخص شده
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr
                          key={item.id}
                          className="border-t last:border-b-0 hover:bg-gray-50"
                        >
                          <td className="p-3 align-middle">
                            {(currentPage - 1) * pageSize + (index + 1)}
                          </td>
                          <td className="p-3 align-middle font-medium">
                            <Link
                              className="text-blue-600 hover:underline"
                              to={`/order-details/${item.order_prfVCodeInt}`}
                            >
                              {" "}
                              {item.order_prf_order_no}
                            </Link>
                          </td>

                          <td className="p-3 align-middle">
                            {formatNumber(item.cost)}
                          </td>
                          <td className="p-3 align-middle">{item.currency}</td>
                          <td className="p-3 align-middle">
                            {formatNumber(item.convertedDollarValue)}
                          </td>
                          <td className="p-3 align-middle">
                            {formatNumber(item.currencyRate)}
                          </td>
                          <td className="p-3 align-middle">
                            {item.convertDate}
                          </td>
                          <td className="p-3 align-middle">
                            {formatNumber(item.usedCost)}
                          </td>
                          <td className="p-3 align-middle">
                            {formatNumber(item.clearanceCost)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-3">
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    pageSizeOptions={[10, 20, 50, 100, 200]}
                    hasNext={hasNext}
                    hasPrevious={hasPrevious}
                    onPageChange={handlePageChange}
                    onNextPage={handleNextPage}
                    onPreviousPage={handlePreviousPage}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default QuotaPermitedList;
